import { Component, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppStateService, GlobalsService } from './core/services';
import { AppState } from './core/models';
import { ToastHostComponent } from './shared/components/toast/toast-host.component';

/**
 * Root shell for the GUEST-ONLY build (`src/main.guest.ts`).
 *
 * This build ships exclusively the public "view shared draft" experience —
 * no topbar, no impersonation banner, no support dialog, no session check.
 * It deliberately does NOT reuse `App` (`app.ts`), whose template/imports pull
 * in the full photographer chrome (TopbarComponent → products/cart/orders…).
 *
 * The only responsibility here is to initialise ngx-translate from the URL
 * locale before the guest route renders, then host the router outlet.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastHostComponent, TranslateModule],
  // Guest build has no reliable fixed global header, so the persiana host
  // anchors at the very top of the viewport (--toast-header-top: 0).
  template: '<router-outlet /><app-toast-host />',
  styles: [':host { --toast-header-top: 0px; }']
})
export class AppGuest implements OnInit {
  private translate = inject(TranslateService);
  private appState = inject(AppStateService);
  private globals = inject(GlobalsService);

  ngOnInit(): void {
    // Guests are always in the public preview state — no auth flow runs here.
    this.appState.setState(AppState.GUEST_PREVIEW);

    this.translate.setDefaultLang('pt_PT');

    // Same <html lang> sync as the main app (app.ts initTranslations) —
    // guests get share-defined languages via translate.use() too.
    this.translate.onLangChange.subscribe(event => {
      document.documentElement.lang = (event.lang || 'pt_PT').replace('_', '-');
    });

    // Prefer locale from the URL, then any saved locale, then fallback.
    const pathLocale = this.getLocaleFromPath(window.location.pathname || '');
    const locale = pathLocale || this.appState.locale() || 'pt_PT';
    if (pathLocale && this.appState.locale() !== pathLocale) {
      this.appState.setLocale(pathLocale);
    }
    this.translate.use(locale);
  }

  private getLocaleFromPath(path: string): string | null {
    if (!path) {
      return null;
    }
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) {
      return null;
    }
    const candidate = segments[0];
    const supportedLocales = this.globals.languages.map(language => language.locale);
    return supportedLocales.includes(candidate) ? candidate : null;
  }
}
